/**
 * timelineStops — the named moments the timeline can jump to.
 *
 * A hand-dated site (public/data/site-dates.json, hooks/useSiteDates.ts)
 * knows more than an era: when it was built, and sometimes when it was left.
 * Those are the events a reader wants to step through, and they are what
 * makes a shared year worth sending on. This turns the table plus whatever
 * pins are loaded into a sorted list of stops, taking each name from the
 * register rather than repeating it in the table. Empty where no hand-dated
 * site is on screen, which is everywhere except Orkney today.
 *
 * Pure. App.tsx builds the list, components/TimelineBar.tsx renders it.
 */
import type { SiteDate } from "../hooks/useGeoJsonLayers.ts";

export interface TimelineStop {
  id: string;
  /** The year the stop jumps to. */
  year: number;
  /** The site's name, as its register writes it. */
  name: string;
  kind: "built" | "left";
}

/**
 * More than this and the row stops being a sequence and starts being a list.
 * The table is thirteen rows today; the cap is here so a filled one cannot
 * quietly turn the bar into a wall of names.
 */
export const MAX_STOPS = 24;

export function stopsFor(
  features: GeoJSON.Feature[],
  siteDates: Record<string, SiteDate>,
): TimelineStop[] {
  if (!features.length || !Object.keys(siteDates).length) return [];
  const seen = new Set<string>();
  const out: TimelineStop[] = [];
  for (const f of features) {
    const p = f.properties;
    const id = p?.id == null ? "" : String(p.id);
    const d = siteDates[id];
    const name = typeof p?.name === "string" ? p.name.trim() : "";
    if (!d || !name) continue;
    // Two sites can be one place in the register (Barnhouse is two records),
    // so the key is what the reader sees, not the id.
    const add = (year: number, kind: TimelineStop["kind"]) => {
      const key = `${name}|${year}|${kind}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ id, year, name, kind });
    };
    // The end of the build span, not the start: that is the year the site
    // certainly stood, which is what a reader clicking its name expects.
    if (typeof d.y1 === "number") add(d.y1, "built");
    if (typeof d.y2 === "number") add(d.y2, "left");
  }
  return out.sort((a, b) => a.year - b.year || a.name.localeCompare(b.name)).slice(0, MAX_STOPS);
}
